/**
 * Shared helpers for the roles themselves, as opposed to the permission codes
 * inside them (see `permissions.ts`). Consumed by every surface that lists or
 * assigns a company role: the create-user dropdown, the user-detail Permissions
 * tab, the bulk-assign drawer, and the Roles & Permissions admin page.
 */

/**
 * Roles only MedScout staff may hand out.
 *
 * `bulk_exporter` carries a single permission — `export.providers_in_territory.all`,
 * a whole-territory data export — which is MedScout's to grant rather than a
 * customer's, so CS asked for it out of the customer-facing pickers (MKT-527).
 *
 * `medscout_ai` carries `read.ai.company` and is MedScout's to grant on the same
 * footing: a License Manager should not be able to switch Scout AI on for a user
 * from the role pickers.
 *
 * `territory_optimization` carries `read.territory_optimization.company` and sits
 * alongside `medscout_ai` for the same reason — it is the entitlement for another
 * AI feature, so turning it on for a user is MedScout's call, not a License
 * Manager's (MKT-545).
 *
 * All three are *global* roles (`company: null` on the API side), so the roles
 * endpoint returns them to every company that can read roles; there is nothing
 * company-scoped to filter them out for us.
 */
export const STAFF_ONLY_ROLE_CODES: readonly string[] = [
  'bulk_exporter',
  'medscout_ai',
  'territory_optimization',
];

/** Whether a role is MedScout's to assign rather than the company's. */
export const isStaffOnlyRole = (role?: MedScout.Role | null): boolean =>
  !!role?.code && STAFF_ONLY_ROLE_CODES.includes(role.code);

/**
 * Whether a role was built for this company rather than shipped by MedScout.
 *
 * `editable` is not a proxy for "custom" — it *is* the flag. On the API side
 * `RoleSerializer.get_editable` returns `obj.company is not None`, and a role
 * with a company is by definition one somebody created for that company; the
 * standard roles are seeded global (`company = None`). There is no separate
 * `is_custom` column on the `Role` model, so this boolean is the whole of what
 * the database knows (MKT-530).
 */
export const isCustomRole = (role?: MedScout.Role | null): boolean =>
  role?.editable === true;

/**
 * Splits a roles list into MedScout's standard set and the company's own, for
 * the section headings the role lists use instead of a per-role marker
 * (MKT-530). Order within each group is preserved, so a caller that sorted its
 * input keeps that sorting.
 *
 * `hasCustomRoles` is the signal for whether to show headings at all: on an
 * account with no custom roles a lone "System roles" banner over the whole list
 * is noise, so those accounts get a flat list.
 */
export const partitionRolesBySource = (
  roles: MedScout.Role[] = []
): {
  standardRoles: MedScout.Role[];
  customRoles: MedScout.Role[];
  hasCustomRoles: boolean;
} => {
  const standardRoles: MedScout.Role[] = [];
  const customRoles: MedScout.Role[] = [];

  roles.forEach((role) =>
    (isCustomRole(role) ? customRoles : standardRoles).push(role)
  );

  return { standardRoles, customRoles, hasCustomRoles: customRoles.length > 0 };
};

/**
 * Narrows a roles list to the ones the viewer may assign.
 *
 * Use this for what gets *rendered* only. Never resolve a role a user already
 * holds through this list — a hidden role has to survive a save, and looking it
 * up in the filtered list would drop it from the payload.
 *
 * Always returns a new array, including for staff, so a caller is free to sort
 * the result without mutating the RTK Query cache it came from.
 */
export const filterAssignableRoles = (
  roles: MedScout.Role[] = [],
  isStaff: boolean
): MedScout.Role[] =>
  isStaff ? [...roles] : roles.filter((role) => !isStaffOnlyRole(role));
